// Access control, UI side (migration 078).
//
// The database is the source of truth and enforces everything (RLS, guard
// triggers, authorize_action). This module is what the UI uses to decide what to
// SHOW: it mirrors the permission catalog and the action map, and turns the
// `my_access()` payload into can() / canInInbox() answers. Hiding a button is a
// courtesy; the server refuses the action regardless.
//
// Pure on purpose (no React, no Supabase, no DOM) so the root vitest suite can
// import it. tests/permissions.test.ts has a drift guard that parses the seed
// rows in 078_access_control.sql and fails if this catalog or ACTION_PERMISSION
// disagree with them.

// ── Catalog ─────────────────────────────────────────────────────────────────

export type PermissionScope = 'workspace' | 'inbox';

export interface PermissionDef {
  key: string;
  scope: PermissionScope;
  group: string;
  label: string;
  description: string;
}

// Mirrors the seed in supabase/migrations/078_access_control.sql (same order).
export const PERMISSIONS: readonly PermissionDef[] = [
  { key: 'workspace.all_inboxes', scope: 'workspace', group: 'inboxes', label: 'All inboxes', description: 'Every inbox permission in every inbox, including confidential and future inboxes' },
  { key: 'inboxes.manage', scope: 'workspace', group: 'inboxes', label: 'Manage inboxes', description: 'Create and edit inboxes, connect Gmail and sync an inbox to n8n' },
  { key: 'team.view', scope: 'workspace', group: 'team', label: 'View team admin', description: 'See the Team, Roles and Access pages and everyones roles and inbox access' },
  { key: 'team.invite', scope: 'workspace', group: 'team', label: 'Invite members', description: 'Add new teammates, with roles you could grant yourself' },
  { key: 'team.manage', scope: 'workspace', group: 'team', label: 'Manage members', description: 'Edit profiles, change workspace roles, deactivate, set expiry and manage groups' },
  { key: 'team.manage_access', scope: 'workspace', group: 'team', label: 'Manage inbox access', description: 'Give, change or remove inbox roles, only roles you hold yourself in that inbox' },
  { key: 'roles.manage', scope: 'workspace', group: 'team', label: 'Manage roles', description: 'Create, clone, edit and delete custom roles' },
  { key: 'audit.view', scope: 'workspace', group: 'team', label: 'View access log', description: 'See who changed roles, inbox access and invites, and when' },
  { key: 'team.view_recaps', scope: 'workspace', group: 'team', label: 'View team recaps', description: 'View other teammates day recaps' },
  { key: 'kb.manage', scope: 'workspace', group: 'library', label: 'Edit knowledge base', description: 'Create, edit and delete knowledge base articles' },
  { key: 'templates.manage_shared', scope: 'workspace', group: 'library', label: 'Edit shared templates', description: 'Create and edit shared reply templates (private templates are always the owners)' },
  { key: 'contacts.edit', scope: 'workspace', group: 'contacts', label: 'Edit contacts', description: 'Edit contact details for contacts you can see' },
  { key: 'contacts.manage_all', scope: 'workspace', group: 'contacts', label: 'All contacts', description: 'See every contact and edit or delete anyones contact notes' },
  { key: 'sla.manage', scope: 'workspace', group: 'config', label: 'Edit SLA rules', description: 'Change SLA targets and rules' },
  { key: 'automations.manage', scope: 'workspace', group: 'config', label: 'Manage automations', description: 'Create and edit automation rules and use AI rule builder and classification' },
  { key: 'settings.manage', scope: 'workspace', group: 'config', label: 'System settings', description: 'Change system settings such as auto follow-up days and view system events' },
  { key: 'company.manage', scope: 'workspace', group: 'config', label: 'Company branding', description: 'Change the company name, logo and branding' },
  { key: 'leave.approve', scope: 'workspace', group: 'people', label: 'Approve leave', description: 'Approve or reject leave requests and receive leave notifications' },
  { key: 'tickets.view', scope: 'inbox', group: 'tickets', label: 'View tickets', description: 'See tickets, messages, events, notes and assist sessions in this inbox' },
  { key: 'tickets.reply', scope: 'inbox', group: 'tickets', label: 'Reply', description: 'Reply and forward from this inbox, and use AI draft and polish' },
  { key: 'tickets.create', scope: 'inbox', group: 'tickets', label: 'Create tickets', description: 'Compose new tickets from this inbox' },
  { key: 'tickets.claim', scope: 'inbox', group: 'tickets', label: 'Claim tickets', description: 'Claim unassigned or own tickets (also when replying to an unassigned ticket)' },
  { key: 'tickets.assign_others', scope: 'inbox', group: 'tickets', label: 'Reassign tickets', description: 'Assign tickets to others, unassign, or take a ticket from someone else' },
  { key: 'tickets.status', scope: 'inbox', group: 'tickets', label: 'Change status', description: 'Resolve, close, reopen, escalate, set priority and snooze' },
  { key: 'tickets.merge', scope: 'inbox', group: 'tickets', label: 'Merge tickets', description: 'Merge tickets (needed on both the source and the target inbox)' },
  { key: 'tickets.archive', scope: 'inbox', group: 'tickets', label: 'Archive tickets', description: 'Archive and unarchive tickets' },
  { key: 'notes.write', scope: 'inbox', group: 'collab', label: 'Write notes', description: 'Add, edit and delete your own internal notes' },
  { key: 'watchers.manage_others', scope: 'inbox', group: 'collab', label: 'Manage watchers', description: 'Add or remove teammates as watchers on tickets' },
  { key: 'assist.start', scope: 'inbox', group: 'collab', label: 'Live customer assist', description: 'Start and end live customer assist sessions' },
  { key: 'journeys.export', scope: 'inbox', group: 'data', label: 'Export journeys', description: 'Export a client journey' },
  { key: 'inbox.view_reports', scope: 'inbox', group: 'data', label: 'Reports', description: 'Include this inbox in Reports' },
];

/** Display order and names of the groups. */
export const PERMISSION_GROUPS: ReadonlyArray<{ key: string; label: string; scope: PermissionScope }> = [
  { key: 'inboxes', label: 'Inboxes', scope: 'workspace' },
  { key: 'team', label: 'Team and access', scope: 'workspace' },
  { key: 'library', label: 'Library', scope: 'workspace' },
  { key: 'contacts', label: 'Contacts', scope: 'workspace' },
  { key: 'config', label: 'Configuration', scope: 'workspace' },
  { key: 'people', label: 'People', scope: 'workspace' },
  { key: 'tickets', label: 'Tickets', scope: 'inbox' },
  { key: 'collab', label: 'Collaboration', scope: 'inbox' },
  { key: 'data', label: 'Data', scope: 'inbox' },
];

export const WORKSPACE_PERMISSION_KEYS: readonly string[] = PERMISSIONS.filter(
  (p) => p.scope === 'workspace',
).map((p) => p.key);
export const INBOX_PERMISSION_KEYS: readonly string[] = PERMISSIONS.filter(
  (p) => p.scope === 'inbox',
).map((p) => p.key);

const BY_KEY = new Map(PERMISSIONS.map((p) => [p.key, p]));

export function permissionDef(key: string): PermissionDef | undefined {
  return BY_KEY.get(key);
}

export function permissionLabel(key: string): string {
  return BY_KEY.get(key)?.label ?? key;
}

/** The built-in role slugs (locked in the database). */
export const SYSTEM_ROLE_SLUGS = [
  'owner',
  'admin',
  'manager',
  'agent',
  'limited',
  'inbox_manager',
  'inbox_agent',
  'inbox_viewer',
] as const;
export type SystemRoleSlug = (typeof SYSTEM_ROLE_SLUGS)[number];

/** Groups a set of permission keys for display, in catalog order. */
export function groupPermissions(
  keys: Iterable<string>,
  scope?: PermissionScope,
): Array<{ group: string; label: string; items: PermissionDef[] }> {
  const wanted = new Set(keys);
  const out: Array<{ group: string; label: string; items: PermissionDef[] }> = [];
  for (const g of PERMISSION_GROUPS) {
    if (scope && g.scope !== scope) continue;
    const items = PERMISSIONS.filter((p) => p.group === g.key && wanted.has(p.key));
    if (items.length > 0) out.push({ group: g.key, label: g.label, items });
  }
  return out;
}

// ── The caller's access (my_access() payload) ───────────────────────────────

export interface InboxAccess {
  role: { id: string; slug: string | null; name: string } | null;
  via: 'grant' | 'all_inboxes';
  expires_at: string | null;
  perms: string[];
}

export interface AccessPayload {
  member: { slack_user_id: string; name: string; email: string };
  workspace_role: { id: string; slug: string | null; name: string; is_owner: boolean };
  access_expires_at: string | null;
  workspace: string[];
  all_inboxes: boolean;
  inboxes: Record<string, InboxAccess>;
  catalog_version: number;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}
function strArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

/** my_access() returns untyped jsonb (or null for a non-member). Never throws. */
export function parseAccess(raw: unknown): AccessPayload | null {
  if (!isRecord(raw)) return null;
  const member = raw.member;
  const wr = raw.workspace_role;
  if (!isRecord(member) || typeof member.slack_user_id !== 'string') return null;
  if (!isRecord(wr) || typeof wr.id !== 'string') return null;

  const inboxes: Record<string, InboxAccess> = {};
  if (isRecord(raw.inboxes)) {
    for (const [id, v] of Object.entries(raw.inboxes)) {
      if (!isRecord(v)) continue;
      const role = isRecord(v.role) && typeof v.role.id === 'string'
        ? {
            id: v.role.id,
            slug: typeof v.role.slug === 'string' ? v.role.slug : null,
            name: typeof v.role.name === 'string' ? v.role.name : '',
          }
        : null;
      inboxes[id] = {
        role,
        via: v.via === 'all_inboxes' ? 'all_inboxes' : 'grant',
        expires_at: typeof v.expires_at === 'string' ? v.expires_at : null,
        perms: strArray(v.perms),
      };
    }
  }

  return {
    member: {
      slack_user_id: member.slack_user_id,
      name: typeof member.name === 'string' ? member.name : '',
      email: typeof member.email === 'string' ? member.email : '',
    },
    workspace_role: {
      id: wr.id,
      slug: typeof wr.slug === 'string' ? wr.slug : null,
      name: typeof wr.name === 'string' ? wr.name : '',
      is_owner: wr.is_owner === true,
    },
    access_expires_at: typeof raw.access_expires_at === 'string' ? raw.access_expires_at : null,
    workspace: strArray(raw.workspace),
    all_inboxes: raw.all_inboxes === true,
    inboxes,
    catalog_version: typeof raw.catalog_version === 'number' ? raw.catalog_version : 1,
  };
}

/** A workspace-level permission. */
export function can(a: AccessPayload | null | undefined, key: string): boolean {
  return !!a && a.workspace.includes(key);
}

/** An inbox-level permission in ONE inbox (all_inboxes grants every one of them). */
export function canInInbox(
  a: AccessPayload | null | undefined,
  inboxId: string | null | undefined,
  key: string,
): boolean {
  if (!a || !inboxId) return false;
  if (a.all_inboxes) return true;
  return a.inboxes[inboxId]?.perms.includes(key) ?? false;
}

/** Any inbox at all (used where there is no inbox yet, e.g. the compose button). */
export function canInAnyInbox(a: AccessPayload | null | undefined, key: string): boolean {
  if (!a) return false;
  if (a.all_inboxes) return true;
  return Object.values(a.inboxes).some((i) => i.perms.includes(key));
}

/** Of `inboxIds`, the ones where the caller holds `key`. */
export function inboxesWith(
  a: AccessPayload | null | undefined,
  key: string,
  inboxIds: readonly string[],
): string[] {
  return inboxIds.filter((id) => canInInbox(a, id, key));
}

// ── Ticket actions → permissions ────────────────────────────────────────────

// Mirrors action_permissions in 078 (n8n and the dispatcher enforce it).
export const ACTION_PERMISSION: Readonly<Record<string, string>> = {
  claim: 'tickets.claim',
  reassign: 'tickets.assign_others',
  resolve: 'tickets.status',
  close: 'tickets.status',
  reopen: 'tickets.status',
  escalate: 'tickets.status',
  set_priority: 'tickets.status',
  snooze_1h: 'tickets.status',
  snooze_4h: 'tickets.status',
  snooze_tomorrow: 'tickets.status',
  snooze_monday: 'tickets.status',
  snooze_2d: 'tickets.status',
  snooze_custom: 'tickets.status',
  unsnooze: 'tickets.status',
  merge: 'tickets.merge',
  archive: 'tickets.archive',
  unarchive: 'tickets.archive',
  reply: 'tickets.reply',
  compose: 'tickets.create',
  assist_start: 'assist.start',
  assist_end: 'assist.start',
  journey_export: 'journeys.export',
};

export interface ActionContext {
  actorSlackId: string | null | undefined;
  /** Who holds the ticket now. */
  assigneeSlackId?: string | null;
  /** For `reassign`: who it is being given to (null/'' = unassign). */
  reassignTo?: string | null;
}

/**
 * The inbox permissions an action needs, exactly as authorize_action computes
 * them: claiming a ticket someone else holds also needs tickets.assign_others,
 * and reassigning to yourself is a claim.
 */
export function permissionsForAction(action: string, ctx: ActionContext): string[] {
  const base = ACTION_PERMISSION[action];
  if (!base) return [];
  const holder = ctx.assigneeSlackId ?? null;
  const takesFromOther = !!holder && holder !== ctx.actorSlackId;

  if (action === 'claim') {
    return takesFromOther ? ['tickets.claim', 'tickets.assign_others'] : ['tickets.claim'];
  }
  if (action === 'reassign') {
    const target = ctx.reassignTo || null;
    if (target && target === ctx.actorSlackId) {
      return takesFromOther ? ['tickets.claim', 'tickets.assign_others'] : ['tickets.claim'];
    }
    return ['tickets.assign_others'];
  }
  return [base];
}

/** True when the caller holds every permission the action needs in that inbox. */
export function canDoAction(
  a: AccessPayload | null | undefined,
  inboxId: string | null | undefined,
  action: string,
  ctx: ActionContext,
): boolean {
  const need = permissionsForAction(action, ctx);
  if (need.length === 0) return false;
  return need.every((k) => canInInbox(a, inboxId, k));
}

// ── Role-assignment rules (UI mirror of the SQL guards) ─────────────────────

export function isSubset(needed: Iterable<string>, held: Iterable<string>): boolean {
  const have = new Set(held);
  for (const k of needed) if (!have.has(k)) return false;
  return true;
}

/**
 * May the caller give someone this workspace role? Owners may give any role;
 * everyone else may not give Owner, and only roles whose permissions they hold.
 * (can_assign_workspace_role in 078.)
 */
export function canAssignWorkspaceRole(
  a: AccessPayload | null | undefined,
  role: { is_owner: boolean },
  rolePermissions: readonly string[],
): boolean {
  if (!a) return false;
  if (role.is_owner) return a.workspace_role.is_owner;
  if (a.workspace_role.is_owner) return true;
  return isSubset(rolePermissions, a.workspace);
}

/**
 * May the caller give this inbox role in this inbox? Needs team.manage_access and
 * every permission of the role held in that same inbox. (can_assign_inbox_role.)
 */
export function canAssignInboxRole(
  a: AccessPayload | null | undefined,
  inboxId: string,
  rolePermissions: readonly string[],
): boolean {
  if (!a || !can(a, 'team.manage_access')) return false;
  return rolePermissions.every((k) => canInInbox(a, inboxId, k));
}

export function diffRolePermissions(
  before: readonly string[],
  after: readonly string[],
): { add: string[]; remove: string[] } {
  const b = new Set(before);
  const a = new Set(after);
  return {
    add: [...a].filter((k) => !b.has(k)).sort(),
    remove: [...b].filter((k) => !a.has(k)).sort(),
  };
}

export interface InboxRoleAssignment {
  inbox_id: string;
  role_id: string;
  expires_at: string | null;
}

/** What changed between two sets of inbox grants (drives set_member_access). */
export function diffInboxRoles(
  before: readonly InboxRoleAssignment[],
  after: readonly InboxRoleAssignment[],
): { grant: InboxRoleAssignment[]; change: InboxRoleAssignment[]; revoke: string[] } {
  const b = new Map(before.map((r) => [r.inbox_id, r]));
  const a = new Map(after.map((r) => [r.inbox_id, r]));
  const grant: InboxRoleAssignment[] = [];
  const change: InboxRoleAssignment[] = [];
  for (const [id, next] of a) {
    const prev = b.get(id);
    if (!prev) grant.push(next);
    else if (prev.role_id !== next.role_id || (prev.expires_at ?? null) !== (next.expires_at ?? null)) {
      change.push(next);
    }
  }
  const revoke = [...b.keys()].filter((id) => !a.has(id)).sort();
  return { grant, change, revoke };
}

// ── Members ─────────────────────────────────────────────────────────────────

export type MemberStatus = 'active' | 'invited' | 'expiring' | 'expired' | 'deactivated';

const DAY_MS = 24 * 60 * 60 * 1000;
export const EXPIRING_SOON_DAYS = 7;

export function memberStatus(
  m: { active: boolean; joined_at?: string | null; access_expires_at?: string | null },
  now: number = Date.now(),
): MemberStatus {
  if (!m.active) return 'deactivated';
  if (m.access_expires_at) {
    const at = Date.parse(m.access_expires_at);
    if (Number.isFinite(at)) {
      if (at <= now) return 'expired';
      if (!m.joined_at) return 'invited';
      if (at - now <= EXPIRING_SOON_DAYS * DAY_MS) return 'expiring';
      return 'active';
    }
  }
  return m.joined_at ? 'active' : 'invited';
}

// ── Dates: a date-only pick ends at 23:59:59 Sydney time ────────────────────

/** Minutes the zone is ahead of UTC at that instant (DST-aware). */
function tzOffsetMinutes(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
}

/**
 * "2026-11-30" -> the ISO instant of 23:59:59 on that calendar day in `timeZone`
 * (default Australia/Sydney, CLAUDE.md rule 7). Returns null for a bad date.
 */
export function expiryEndOfDay(dateStr: string, timeZone = 'Australia/Sydney'): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const check = new Date(Date.UTC(y, mo - 1, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) {
    return null;
  }
  // Wall-clock 23:59:59 interpreted as UTC, then corrected by the zone's offset
  // at that instant (twice, so a DST change on that day settles).
  const wall = Date.UTC(y, mo - 1, d, 23, 59, 59);
  let guess = wall - tzOffsetMinutes(new Date(wall), timeZone) * 60000;
  guess = wall - tzOffsetMinutes(new Date(guess), timeZone) * 60000;
  return new Date(guess).toISOString();
}

/** ISO instant -> "YYYY-MM-DD" in the zone (for filling a date input). */
export function toDateInput(iso: string | null | undefined, timeZone = 'Australia/Sydney'): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(t));
  return parts; // en-CA is YYYY-MM-DD
}

// ── Audit log wording ───────────────────────────────────────────────────────

export interface AuditRow {
  id?: number;
  at: string;
  actor_slack_id: string | null;
  source: 'spa' | 'service' | 'cron';
  action: string;
  target_slack_id: string | null;
  inbox_id: string | null;
  role_id?: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  note?: string | null;
}

export interface AuditLookups {
  members: ReadonlyMap<string, string>;
  inboxes: ReadonlyMap<string, string>;
  roles: ReadonlyMap<string, string>;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** One readable sentence for an audit row. Unknown actions fall back to the code. */
export function describeAuditEvent(e: AuditRow, l: AuditLookups): string {
  const who = e.actor_slack_id
    ? (l.members.get(e.actor_slack_id) ?? 'Someone')
    : e.source === 'cron'
      ? 'The system'
      : 'The system';
  const target = e.target_slack_id ? (l.members.get(e.target_slack_id) ?? 'a teammate') : 'a teammate';
  const inbox = e.inbox_id ? (l.inboxes.get(e.inbox_id) ?? 'an inbox') : 'an inbox';
  const roleName = (side: Record<string, unknown> | null) =>
    str(side?.role) || (e.role_id ? (l.roles.get(e.role_id) ?? '') : '') || 'a role';

  switch (e.action) {
    case 'member.invited':
      return `${who} invited ${target} as ${roleName(e.after)}`;
    case 'member.role_changed':
      return `${who} changed ${target} from ${roleName(e.before)} to ${roleName(e.after)}`;
    case 'member.deactivated':
      return `${who} deactivated ${target}`;
    case 'member.reactivated':
      return `${who} reactivated ${target}`;
    case 'member.expired':
      return `${target}'s access expired and was switched off`;
    case 'member.expiry_set': {
      const next = str(e.after?.access_expires_at);
      return next
        ? `${who} set ${target}'s access to end on ${toDateInput(next)}`
        : `${who} removed ${target}'s access expiry`;
    }
    case 'member.email_changed':
      return `${who} changed ${target}'s sign-in email`;
    case 'member.deleted':
      return `${who} removed ${str(e.before?.name) || target}`;
    case 'inbox_access.granted':
      return `${who} gave ${target} the ${roleName(e.after)} role in ${inbox}`;
    case 'inbox_access.role_changed':
      return `${who} changed ${target} in ${inbox} from ${roleName(e.before)} to ${roleName(e.after)}`;
    case 'inbox_access.revoked':
      return `${who} removed ${target}'s access to ${inbox}`;
    case 'inbox_access.expired':
      return `${target}'s access to ${inbox} expired`;
    case 'role.created':
      return `${who} created the role ${str(e.after?.name) || 'a role'}`;
    case 'role.updated':
      return `${who} renamed or edited the role ${str(e.after?.name) || str(e.before?.name) || 'a role'}`;
    case 'role.deleted':
      return `${who} deleted the role ${str(e.before?.name) || 'a role'}`;
    case 'role.permission_added':
      return `${who} added "${permissionLabel(str(e.after?.permission))}" to ${l.roles.get(e.role_id ?? '') ?? 'a role'}`;
    case 'role.permission_removed':
      return `${who} removed "${permissionLabel(str(e.before?.permission))}" from ${l.roles.get(e.role_id ?? '') ?? 'a role'}`;
    case 'invite.sent':
      return `${who} sent ${target} an invite email`;
    default:
      return `${who}: ${e.action}${e.target_slack_id ? ` (${target})` : ''}`;
  }
}
